import { HERO_IMAGE, REGULAR_PRICE, SALE_PRICE } from '../data/content'
import { HERO_SIZES, HERO_WIDTHS, optimizedImage, optimizedSrcSet } from './images'

const TITLE = 'Industrial Instrumentation Instructor System 2026 | Ready-to-Teach Curriculum'
const DESCRIPTION =
  'The all-in-one curriculum for automation educators: 6 technical volumes and 6 instructor bonuses aligned with ANSI, ISA-5.1, IEEE and OSHA. Cut lesson planning time by 80%. One-time $27.'
const IMAGE_ORIGINS = ['https://wsrv.nl', 'https://cdn.magicpatterns.com']

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function appendLink(attributes: Record<string, string>) {
  const link = document.createElement('link')
  Object.entries(attributes).forEach(([key, value]) => link.setAttribute(key, value))
  document.head.appendChild(link)
  return link
}

/**
 * Runs before React renders so the LCP image and the fonts are discovered by the
 * preload scanner as early as possible, and so crawlers get a complete head.
 */
export function setupPage() {
  document.documentElement.lang = 'en'
  document.title = TITLE

  upsertMeta('name', 'description', DESCRIPTION)
  upsertMeta('name', 'viewport', 'width=device-width, initial-scale=1, viewport-fit=cover')
  upsertMeta('name', 'theme-color', '#05090f')
  upsertMeta('name', 'robots', 'index, follow')

  upsertMeta('property', 'og:type', 'product')
  upsertMeta('property', 'og:title', TITLE)
  upsertMeta('property', 'og:description', DESCRIPTION)
  upsertMeta('property', 'og:image', optimizedImage(HERO_IMAGE, 1200, 72))
  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', TITLE)
  upsertMeta('name', 'twitter:description', DESCRIPTION)
  upsertMeta('name', 'twitter:image', optimizedImage(HERO_IMAGE, 1200, 72))

  if (!document.head.querySelector('link[rel="canonical"]')) {
    appendLink({ rel: 'canonical', href: window.location.origin + window.location.pathname })
  }

  // Warm up the image proxy and the CDN before the markup asks for them.
  IMAGE_ORIGINS.forEach((href) => {
    appendLink({ rel: 'preconnect', href, crossorigin: '' })
    appendLink({ rel: 'dns-prefetch', href })
  })

  // LCP image: same URL, srcset and sizes as the <img> in the hero.
  appendLink({
    rel: 'preload',
    as: 'image',
    href: optimizedImage(HERO_IMAGE, 640),
    imagesrcset: optimizedSrcSet(HERO_IMAGE, HERO_WIDTHS),
    imagesizes: HERO_SIZES,
    fetchpriority: 'high',
  })

  // Fonts, loaded without blocking the first paint.
  appendLink({ rel: 'preconnect', href: 'https://fonts.googleapis.com' })
  appendLink({ rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' })
  const fonts = appendLink({
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap',
    media: 'print',
  })
  fonts.addEventListener('load', () => fonts.setAttribute('media', 'all'), { once: true })

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Industrial Instrumentation Instructor System 2026',
    description: DESCRIPTION,
    image: [optimizedImage(HERO_IMAGE, 1200, 72)],
    brand: { '@type': 'Brand', name: 'IIIS 2026' },
    category: 'Educational digital curriculum',
    offers: {
      '@type': 'Offer',
      price: String(SALE_PRICE),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: window.location.href,
      priceValidUntil: '2026-12-31',
      highPrice: String(REGULAR_PRICE),
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '128',
    },
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(schema)
  document.head.appendChild(script)
}
